import React, { useEffect, useState, useRef } from 'react';
import { useDrawers } from '../../contexts/DrawerContext';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { UploadCloud, File, X, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
type UploadState = 'empty' | 'dragging' | 'selected' | 'uploading' | 'success' | 'error';
const inputClass = 'w-full h-12 px-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition';
const labelClass = 'block text-sm font-medium text-ink mb-1.5';
export function UploadDrawer() {
  const {
    isUploadOpen,
    closeUpload
  } = useDrawers();
  const [uploadState, setUploadState] = useState<UploadState>('empty');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isUploadOpen) {
      setTimeout(() => {
        setUploadState('empty');
        setProgress(0);
      }, 300);
    }
  }, [isUploadOpen]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (uploadState === 'empty' || uploadState === 'error') {
      setUploadState('dragging');
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (uploadState === 'dragging') {
      setUploadState('empty');
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploadState('selected');
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadState('selected');
    }
  };
  const simulateUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (uploadState !== 'selected') return;
    setUploadState('uploading');
    let p = 0;
    const interval = setInterval(() => {
      p += 10;
      setProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        setUploadState('success');
      }
    }, 200);
  };
  return <Drawer isOpen={isUploadOpen} onClose={closeUpload} title="Upload a contract" subtitle={uploadState === 'success' ? undefined : 'Reviewed by a solicitor, not a checklist'} data-id="element-119">
      <div className="p-6" data-id="element-120">
        {uploadState === 'success' ? <div className="py-12 flex flex-col items-center text-center space-y-6 animate-in zoom-in-95 duration-300" data-id="element-121">
            <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center text-accent" data-id="element-122">
              <CheckCircle2 className="w-8 h-8" data-id="element-123" />
            </div>
            <div className="space-y-2" data-id="element-124">
              <h3 className="text-2xl font-serif text-ink" data-id="element-125">
                Document received.
              </h3>
              <p className="text-muted-ink leading-relaxed max-w-xs mx-auto" data-id="element-126">
                The team will review the document and contact you about the
                proposed next step.
              </p>
            </div>
            <Button onClick={closeUpload} variant="outline" className="mt-2" data-id="element-127">
              Close
            </Button>
          </div> : <form onSubmit={simulateUpload} className="space-y-6" data-id="element-128">
            <div className="flex items-start gap-3 rounded-xl bg-accent-soft border border-accent-light p-4" data-id="element-129">
              <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-130" />
              <p className="text-sm text-ink leading-relaxed" data-id="element-131">
                Send your contract or Section 32 and the team will read it
                before you are committed.
              </p>
            </div>

            {/* Drag Drop Zone */}
            <div className={cn('rounded-2xl border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors relative', uploadState === 'dragging' ? 'border-accent bg-accent-soft' : 'border-border bg-surface', uploadState === 'error' && 'border-red-300 bg-red-50', (uploadState === 'selected' || uploadState === 'uploading') && 'border-solid border-border bg-soft-panel')} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => uploadState === 'empty' && fileInputRef.current?.click()} data-id="element-132">
              <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx" onChange={handleFileSelect} data-id="element-133" />

              {uploadState === 'empty' || uploadState === 'dragging' ? <>
                  <div className="w-12 h-12 rounded-2xl bg-accent-soft flex items-center justify-center mb-4" data-id="element-134">
                    <UploadCloud className={cn('w-6 h-6', uploadState === 'dragging' ? 'text-accent' : 'text-accent/80')} data-id="element-135" />
                  </div>
                  <p className="text-ink font-semibold mb-1" data-id="element-136">
                    Click or drag a file to upload
                  </p>
                  <p className="text-xs text-muted-ink" data-id="element-137">
                    Accepted files: PDF, DOC, DOCX
                  </p>
                </> : uploadState === 'error' ? <>
                  <AlertCircle className="w-10 h-10 mb-4 text-red-500" data-id="element-138" />
                  <p className="text-ink font-semibold mb-1" data-id="element-139">Upload failed</p>
                  <button type="button" onClick={() => setUploadState('empty')} className="text-sm text-accent font-semibold hover:underline" data-id="element-140">
                    Try again
                  </button>
                </> : <div className="w-full flex items-center gap-4 text-left" data-id="element-141">
                  <div className="bg-surface p-3 rounded-xl border border-border shrink-0" data-id="element-142">
                    <File className="w-6 h-6 text-accent" data-id="element-143" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-144">
                    <p className="text-sm font-semibold text-ink truncate" data-id="element-145">
                      contract_of_sale.pdf
                    </p>
                    <p className="text-xs text-muted-ink" data-id="element-146">
                      {uploadState === 'uploading' ? `Uploading ${progress}%` : '2.4 MB'}
                    </p>
                  </div>
                  {uploadState === 'selected' && <button type="button" onClick={e => {
              e.stopPropagation();
              setUploadState('empty');
            }} className="p-2 rounded-full text-muted-ink hover:text-ink hover:bg-surface" aria-label="Remove file" data-id="element-147">
                      <X className="w-4 h-4" data-id="element-148" />
                    </button>}
                </div>}

              {uploadState === 'uploading' && <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-border rounded-b-2xl overflow-hidden" data-id="element-149">
                  <div className="h-full bg-accent transition-all duration-200 ease-out" style={{
              width: `${progress}%`
            }} data-id="element-150" />
                </div>}
            </div>

            <div className="space-y-4 pt-2" data-id="element-151">
              <div className="grid grid-cols-2 gap-4" data-id="element-152">
                <div data-id="element-153">
                  <label className={labelClass} data-id="element-154">Full name</label>
                  <input required type="text" autoComplete="name" className={inputClass} data-id="element-155" />
                </div>
                <div data-id="element-156">
                  <label className={labelClass} data-id="element-157">Phone</label>
                  <input required type="tel" autoComplete="tel" className={inputClass} data-id="element-158" />
                </div>
              </div>

              <div data-id="element-159">
                <label className={labelClass} data-id="element-160">Email</label>
                <input required type="email" autoComplete="email" className={inputClass} data-id="element-161" />
              </div>

              <div data-id="element-162">
                <label className={labelClass} data-id="element-163">Property address</label>
                <input required type="text" className={inputClass} data-id="element-164" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-165">
                <div data-id="element-166">
                  <label className={labelClass} data-id="element-167">Matter type</label>
                  <select className={`${inputClass} appearance-none`} data-id="element-168">
                    <option data-id="element-169">Buying</option>
                    <option data-id="element-170">Selling</option>
                    <option data-id="element-171">Contract review</option>
                  </select>
                </div>
                <div data-id="element-172">
                  <label className={labelClass} data-id="element-173">Deadline</label>
                  <input type="date" className={inputClass} data-id="element-174" />
                </div>
              </div>

              <div data-id="element-175">
                <label className={labelClass} data-id="element-176">Message</label>
                <textarea rows={2} className="w-full p-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition resize-none" data-id="element-177"></textarea>
              </div>

              <Button fullWidth variant="accent" type="submit" disabled={uploadState !== 'selected'} className="mt-2" data-id="element-178">
                {uploadState === 'uploading' ? 'Uploading...' : 'Submit document'}
              </Button>

              {/* CODE COMMENT: This wording needs legal approval before production. */}
              <p className="text-xs text-muted-ink text-center" data-id="element-179">
                Submitting information does not by itself create a
                solicitor-client relationship. Engagement is confirmed
                separately.
              </p>
            </div>
          </form>}
      </div>
    </Drawer>;
}